import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  usePickemLiveScoring,
  type GradingResult,
  type LiveScoringStore,
} from '@/hooks/usePickemLiveScoring';

const LiveScoringContext = createContext<LiveScoringStore | null>(null);

interface LiveScoringProviderProps {
  pickemId: string;
  children: ReactNode;
}

/**
 * Provides live grading results for a whole pickem from a SINGLE SSE connection.
 *
 * Wrap the list of questions in this provider; each question reads its own
 * grading result via {@link useQuestionGradingResult} instead of opening its own
 * stream. This collapses N per-question SSE connections down to one per pickem.
 *
 * The provider exposes a stable external store (its identity never changes), so
 * an incoming update for one question does not re-render the provider or any
 * sibling questions — only the subscriber for the affected questionId re-renders.
 */
export function LiveScoringProvider({ pickemId, children }: LiveScoringProviderProps) {
  const { store } = usePickemLiveScoring({ pickemId });

  return <LiveScoringContext.Provider value={store}>{children}</LiveScoringContext.Provider>;
}

const NULL_STORE_SUBSCRIBE = () => () => {};

/**
 * Returns the latest grading result for a single question from the shared
 * per-pickem stream, or null if none has streamed yet (or if used outside a
 * {@link LiveScoringProvider}).
 *
 * Subscribes only to updates for the given questionId via `useSyncExternalStore`,
 * so this component re-renders only when its own grading result changes.
 */
export function useQuestionGradingResult(questionId: string): GradingResult | null {
  const store = useContext(LiveScoringContext);
  const subscribe = useCallback(
    (listener: () => void) =>
      store ? store.subscribeQuestion(questionId, listener) : NULL_STORE_SUBSCRIBE(),
    [store, questionId]
  );
  return useSyncExternalStore(subscribe, () => (store ? store.getResult(questionId) : null));
}

/**
 * Returns whether the shared per-pickem SSE stream is currently connected.
 * Subscribes only to connection-state changes, independent of grading updates.
 */
export function useLiveScoringConnected(): boolean {
  const store = useContext(LiveScoringContext);
  return useSyncExternalStore(store ? store.subscribeConnection : NULL_STORE_SUBSCRIBE, () =>
    store ? store.getIsConnected() : false
  );
}
